/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Mail, Phone, Trash2, ArrowRight, UserCheck, Receipt } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const STAGES = [
    { key: "new_lead", label: "New lead", hint: "Just got in touch" },
    { key: "assessed", label: "Assessed", hint: "Services proposed" },
    { key: "quote_sent", label: "Quote sent", hint: "Awaiting reply" },
    { key: "signed", label: "Signed", hint: "ATA returned" },
    { key: "onboarded", label: "Onboarded", hint: "Live client" },
];

const SOURCES = [
    { value: "call", label: "Phone call" },
    { value: "txt", label: "Text" },
    { value: "email", label: "Email" },
    { value: "referral", label: "Referral" },
    { value: "website", label: "Website" },
    { value: "other", label: "Other" },
];

const SERVICE_OPTIONS = [
    "GST returns",
    "Year-end accounts",
    "Income tax returns",
    "Payroll",
    "Bookkeeping",
    "Provisional tax planning",
    "Company setup",
    "Other",
];

export default function Pipeline() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openNew, setOpenNew] = useState(false);
    const [activeLead, setActiveLead] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/leads/");
            setLeads(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const byStage = useMemo(() => {
        const m = Object.fromEntries(STAGES.map((s) => [s.key, []]));
        for (const l of leads) m[l.stage]?.push(l);
        return m;
    }, [leads]);

    async function moveTo(lead, nextStage) {
        try {
            await api.patch(`/leads/${lead.id}`, { stage: nextStage });
            toast.success(`${lead.name} → ${STAGES.find((s) => s.key === nextStage)?.label}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function convertToClient(lead) {
        try {
            const { data } = await api.post(`/leads/${lead.id}/convert`);
            toast.success(data.already_converted ? "Already converted — opening client" : `${lead.name} is now a client`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="pipeline-page">
            <PageHeader
                eyebrow="Onboarding"
                title="Pipeline"
                description="Every prospect on one board — from the first text to a fully onboarded client. Tap a card to update notes; tap the arrow to move it forward."
                testId="pipeline-header"
            >
                <Dialog open={openNew} onOpenChange={setOpenNew}>
                    <DialogTrigger asChild>
                        <Button className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="pipeline-new-btn">
                            <Plus className="h-4 w-4" /> New lead
                        </Button>
                    </DialogTrigger>
                    <NewLeadDialog onCreated={() => { setOpenNew(false); load(); }} />
                </Dialog>
            </PageHeader>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-[#5E636E]"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : leads.length === 0 ? (
                <EmptyState
                    icon={Receipt}
                    title="No leads yet"
                    description="Add the first prospect that contacted you and move them through the pipeline as you go."
                    testId="pipeline-empty"
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pipeline-board">
                    {STAGES.map((stage) => (
                        <div key={stage.key} className="flex flex-col rounded-xl bg-[#F5F4EF] border border-[#E4E4E7] p-3 min-h-[200px]" data-testid={`pipeline-col-${stage.key}`}>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <div className="font-display text-sm font-medium">{stage.label}</div>
                                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#5E636E] mt-0.5">{stage.hint}</div>
                                </div>
                                <span className="rounded-full bg-white border border-[#E4E4E7] px-2 py-0.5 text-[10px] font-medium text-[#5E636E]" data-testid={`pipeline-count-${stage.key}`}>
                                    {byStage[stage.key].length}
                                </span>
                            </div>
                            <div className="space-y-2 flex-1">
                                {byStage[stage.key].map((lead) => (
                                    <LeadCard
                                        key={lead.id}
                                        lead={lead}
                                        stage={stage}
                                        onClick={() => setActiveLead(lead)}
                                        onAdvance={() => {
                                            const idx = STAGES.findIndex((s) => s.key === stage.key);
                                            if (idx < STAGES.length - 1) moveTo(lead, STAGES[idx + 1].key);
                                            else convertToClient(lead);
                                        }}
                                        onConvert={() => convertToClient(lead)}
                                    />
                                ))}
                                {byStage[stage.key].length === 0 && (
                                    <div className="rounded-lg border border-dashed border-[#E4E4E7] py-6 text-center text-[11px] text-[#5E636E]">drop a lead here</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {activeLead && (
                <LeadDetailDialog
                    lead={activeLead}
                    onClose={() => setActiveLead(null)}
                    onSaved={() => { setActiveLead(null); load(); }}
                    onDeleted={() => { setActiveLead(null); load(); }}
                />
            )}
        </div>
    );
}


function LeadCard({ lead, stage, onClick, onAdvance, onConvert }) {
    const isLastStage = stage.key === STAGES[STAGES.length - 1].key;
    return (
        <div
            className="group rounded-lg border border-[#E4E4E7] bg-white p-3 hover:shadow-sm transition-shadow cursor-pointer"
            onClick={onClick}
            data-testid={`lead-card-${lead.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-sm leading-snug">{lead.name}</div>
                <span className="rounded-full bg-[#F5F4EF] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[#5E636E] shrink-0">
                    {SOURCES.find((s) => s.value === lead.source)?.label || lead.source}
                </span>
            </div>
            <div className="mt-2 space-y-0.5 text-[11px] text-[#5E636E]">
                {lead.contact_email && <div className="inline-flex items-center gap-1 mr-2"><Mail className="h-2.5 w-2.5" /> {lead.contact_email}</div>}
                {lead.contact_phone && <div className="inline-flex items-center gap-1"><Phone className="h-2.5 w-2.5" /> {lead.contact_phone}</div>}
            </div>
            {(lead.services_recommended?.length || 0) > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {lead.services_recommended.slice(0, 3).map((s) => (
                        <span key={s} className="rounded bg-[#0E3B2E]/10 text-[#0E3B2E] px-1.5 py-0.5 text-[10px]">{s}</span>
                    ))}
                    {lead.services_recommended.length > 3 && (
                        <span className="text-[10px] text-[#5E636E]">+{lead.services_recommended.length - 3}</span>
                    )}
                </div>
            )}
            {lead.fee_estimate && <div className="mt-2 text-[11px] text-[#0F1115] font-medium">{lead.fee_estimate}</div>}
            <div className="mt-3 flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); isLastStage ? onConvert() : onAdvance(); }}
                    className="inline-flex items-center gap-1 rounded-full bg-[#0E3B2E] text-white px-2.5 py-1 text-[10px] hover:bg-[#0B2F25]"
                    data-testid={`lead-advance-${lead.id}`}
                >
                    {isLastStage ? <><UserCheck className="h-3 w-3" /> Convert to client</> : <>Next <ArrowRight className="h-3 w-3" /></>}
                </button>
            </div>
            {lead.converted_client_id && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-emerald-700"><UserCheck className="h-3 w-3" /> Client created</div>
            )}
        </div>
    );
}


function NewLeadDialog({ onCreated }) {
    const [form, setForm] = useState({ name: "", contact_email: "", contact_phone: "", source: "email", notes: "", current_accountant: "", fee_estimate: "" });
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e?.preventDefault();
        if (!form.name) return;
        setBusy(true);
        try {
            await api.post("/leads/", {
                ...form,
                contact_email: form.contact_email || null,
                contact_phone: form.contact_phone || null,
                notes: form.notes || null,
                current_accountant: form.current_accountant || null,
                fee_estimate: form.fee_estimate || null,
            });
            toast.success(`${form.name} added to pipeline`);
            onCreated?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    function s(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    return (
        <DialogContent className="max-w-lg" data-testid="new-lead-dialog">
            <DialogHeader>
                <DialogTitle>New lead</DialogTitle>
                <DialogDescription>Capture the moment someone makes contact — you can flesh out the rest later.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="new-lead-form">
                <div>
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Name</Label>
                    <Input value={form.name} onChange={(e) => s("name", e.target.value)} required className="mt-1.5" data-testid="lead-name" autoFocus />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Email</Label>
                        <Input type="email" value={form.contact_email} onChange={(e) => s("contact_email", e.target.value)} className="mt-1.5" data-testid="lead-email" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Phone</Label>
                        <Input value={form.contact_phone} onChange={(e) => s("contact_phone", e.target.value)} className="mt-1.5" data-testid="lead-phone" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Source</Label>
                        <Select value={form.source} onValueChange={(v) => s("source", v)}>
                            <SelectTrigger className="mt-1.5" data-testid="lead-source"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {SOURCES.map((src) => <SelectItem key={src.value} value={src.value}>{src.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Current accountant</Label>
                        <Input value={form.current_accountant} onChange={(e) => s("current_accountant", e.target.value)} placeholder="(if any)" className="mt-1.5" data-testid="lead-current-accountant" />
                    </div>
                </div>
                <div>
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Notes / what they&apos;re after</Label>
                    <Textarea rows={3} value={form.notes} onChange={(e) => s("notes", e.target.value)} className="mt-1.5" data-testid="lead-notes" />
                </div>
                <DialogFooter>
                    <Button type="submit" disabled={busy || !form.name} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="lead-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to pipeline"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}


function LeadDetailDialog({ lead, onClose, onSaved, onDeleted }) {
    const [form, setForm] = useState({
        name: lead.name,
        contact_email: lead.contact_email || "",
        contact_phone: lead.contact_phone || "",
        source: lead.source,
        notes: lead.notes || "",
        current_accountant: lead.current_accountant || "",
        fee_estimate: lead.fee_estimate || "",
        services_recommended: lead.services_recommended || [],
        stage: lead.stage,
    });
    const [busy, setBusy] = useState(false);

    function toggleService(svc) {
        setForm((f) => ({
            ...f,
            services_recommended: f.services_recommended.includes(svc)
                ? f.services_recommended.filter((s) => s !== svc)
                : [...f.services_recommended, svc],
        }));
    }

    async function save() {
        setBusy(true);
        try {
            await api.patch(`/leads/${lead.id}`, {
                name: form.name,
                contact_email: form.contact_email || null,
                contact_phone: form.contact_phone || null,
                source: form.source,
                notes: form.notes || null,
                current_accountant: form.current_accountant || null,
                fee_estimate: form.fee_estimate || null,
                services_recommended: form.services_recommended,
                stage: form.stage,
            });
            toast.success("Lead updated");
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function del() {
        if (!window.confirm(`Delete lead "${lead.name}"? This can't be undone.`)) return;
        setBusy(true);
        try {
            await api.delete(`/leads/${lead.id}`);
            toast.success("Lead deleted");
            onDeleted?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    function s(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-2xl" data-testid="lead-detail-dialog">
                <DialogHeader>
                    <DialogTitle>{lead.name}</DialogTitle>
                    <DialogDescription>Update lead details and recommended services. Stage moves work from the board too.</DialogDescription>
                </DialogHeader>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Name</Label>
                        <Input value={form.name} onChange={(e) => s("name", e.target.value)} className="mt-1.5" data-testid="ld-name" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Email</Label>
                        <Input type="email" value={form.contact_email} onChange={(e) => s("contact_email", e.target.value)} className="mt-1.5" data-testid="ld-email" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Phone</Label>
                        <Input value={form.contact_phone} onChange={(e) => s("contact_phone", e.target.value)} className="mt-1.5" data-testid="ld-phone" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Source</Label>
                        <Select value={form.source} onValueChange={(v) => s("source", v)}>
                            <SelectTrigger className="mt-1.5" data-testid="ld-source"><SelectValue /></SelectTrigger>
                            <SelectContent>{SOURCES.map((src) => <SelectItem key={src.value} value={src.value}>{src.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Stage</Label>
                        <Select value={form.stage} onValueChange={(v) => s("stage", v)}>
                            <SelectTrigger className="mt-1.5" data-testid="ld-stage"><SelectValue /></SelectTrigger>
                            <SelectContent>{STAGES.map((st) => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Current accountant</Label>
                        <Input value={form.current_accountant} onChange={(e) => s("current_accountant", e.target.value)} className="mt-1.5" data-testid="ld-current-acct" />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Fee estimate</Label>
                        <Input value={form.fee_estimate} onChange={(e) => s("fee_estimate", e.target.value)} placeholder="e.g. $350/mo" className="mt-1.5" data-testid="ld-fee" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Services recommended</Label>
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ld-services">
                            {SERVICE_OPTIONS.map((svc) => {
                                const on = form.services_recommended.includes(svc);
                                return (
                                    <button
                                        key={svc}
                                        type="button"
                                        onClick={() => toggleService(svc)}
                                        className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "bg-[#0E3B2E] text-white border-[#0E3B2E]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:bg-zinc-50"}`}
                                        data-testid={`ld-svc-${svc.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                        {svc}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Notes</Label>
                        <Textarea rows={4} value={form.notes} onChange={(e) => s("notes", e.target.value)} className="mt-1.5" data-testid="ld-notes" />
                    </div>
                </div>
                <DialogFooter className="justify-between">
                    <Button variant="outline" onClick={del} disabled={busy} className="text-rose-700 hover:text-rose-800 hover:bg-rose-50" data-testid="ld-delete">
                        <Trash2 className="h-4 w-4" /> Delete
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>Cancel</Button>
                        <Button onClick={save} disabled={busy} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="ld-save">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
